<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import StopVehicleGroup from '@/components/transit/StopVehicleGroup.vue'
import { useStopVehiclesQuery } from '@/composables/useStopVehiclesQuery'
import { routeCodesAtStop, useTransitIndex } from '@/composables/useTransitIndex'
import type { BusRoute, DirectionKey, MapVehicle } from '@/types'
import { compareRouteCodes } from '@/utils/routeOrder'
import { resolveVehicleSnapshot } from '@/utils/vehicleSnapshot'

import StopScreenShell from './StopScreenShell.vue'

const props = defineProps<{ stopId: string }>()

const router = useRouter()
const { index } = useTransitIndex()

const numericId = computed(() => {
  const parsed = Number(props.stopId)
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined
})

const routeCodes = computed(() => {
  if (numericId.value === undefined) return []
  const entries = index.value?.routesByStopId.get(numericId.value)
  return entries ? routeCodesAtStop(entries) : []
})

const routes = computed(() => {
  if (numericId.value === undefined) return []
  const entries = index.value?.routesByStopId.get(numericId.value) ?? []
  const seen = new Set<number>()
  const result: BusRoute[] = []
  for (const entry of entries) {
    if (seen.has(entry.route.id)) continue
    seen.add(entry.route.id)
    result.push(entry.route)
  }
  return result
})

const query = useStopVehiclesQuery(numericId, routeCodes, routes)

const snapshot = computed(() =>
  resolveVehicleSnapshot({
    vehicles: query.data.value ?? [],
    isPending: query.isPending.value,
    hasError: Boolean(query.error.value),
  }),
)

const vehicles = computed<MapVehicle[]>(() => snapshot.value.vehicles)

interface VehicleGroup {
  routeCode: string
  direction: DirectionKey
  vehicles: MapVehicle[]
}

/**
 * The query orders its snapshot by ETA for polling consumers, but a stop list
 * answers a different question: which bus is physically nearest to this stop?
 * Sort only within a route/direction group so route sections keep their
 * natural order and do not jump around as the feed refreshes. An explicit
 * source index keeps equal and missing distances stable across runtimes.
 */
function sortGroupVehicles(vehicles: MapVehicle[]) {
  return vehicles
    .map((vehicle, index) => ({ vehicle, index }))
    .sort((left, right) => {
      const leftDistance = left.vehicle.distanceToStop
      const rightDistance = right.vehicle.distanceToStop
      const leftMissing = leftDistance === undefined || !Number.isFinite(leftDistance)
      const rightMissing = rightDistance === undefined || !Number.isFinite(rightDistance)

      if (leftMissing || rightMissing) {
        if (leftMissing !== rightMissing) return leftMissing ? 1 : -1
      } else if (leftDistance !== rightDistance) {
        return leftDistance - rightDistance
      }

      return left.index - right.index
    })
    .map(({ vehicle }) => vehicle)
}

/** Route sections follow natural route order, with outbound before inbound. */
const vehicleGroups = computed<VehicleGroup[]>(() => {
  const groups = new Map<string, VehicleGroup>()
  for (const vehicle of vehicles.value) {
    const key = `${vehicle.routeCode}:${vehicle.direction}`
    const group = groups.get(key)
    if (group) group.vehicles.push(vehicle)
    else
      groups.set(key, {
        routeCode: vehicle.routeCode,
        direction: vehicle.direction,
        vehicles: [vehicle],
      })
  }

  return [...groups.values()]
    .map((group) => ({ ...group, vehicles: sortGroupVehicles(group.vehicles) }))
    .sort((left, right) => {
      const routeOrder = compareRouteCodes(left.routeCode, right.routeCode)
      if (routeOrder !== 0) return routeOrder
      if (left.direction === right.direction) return 0
      return left.direction === 'outbound' ? -1 : 1
    })
})

/**
 * `resolveVehicleSnapshot` reports `unavailable` for BOTH "the relay failed" and
 * "the relay answered, nothing is coming" — it only distinguishes them when it
 * has stale vehicles to fall back on. Those need different words, so the error
 * flag is read directly rather than inferred from the snapshot source.
 */
const emptyMessage = computed(() => {
  if (snapshot.value.source === 'checking') return 'Checking for approaching buses…'
  if (query.isError.value) return 'Live bus data is unavailable right now.'
  if (routeCodes.value.length === 0) return 'No routes serve this stop.'
  return 'No buses are approaching this stop right now.'
})

/** Route names come from the cached dataset, not from the vehicle feed. */
function routeName(code: string) {
  return index.value?.routesByCode.get(code)?.name
}

function trackVehicle(vehicle: MapVehicle) {
  router.push({
    name: 'bus',
    params: { routeCode: vehicle.routeCode, vehicleId: vehicle.id },
    query: { stop: props.stopId },
  })
}
</script>

<template>
  <StopScreenShell :stop-id="stopId">
    <div>
      <p
        v-if="snapshot.source === 'last-known'"
        class="border-b bg-muted px-3 py-2 text-xs text-muted-foreground"
      >
        Connection lost — showing the last known positions.
      </p>

      <div v-if="vehicleGroups.length > 0">
        <StopVehicleGroup
          v-for="group in vehicleGroups"
          :key="`${group.routeCode}:${group.direction}`"
          :route-code="group.routeCode"
          :route-name="routeName(group.routeCode)"
          :direction="group.direction"
          :vehicles="group.vehicles"
          @select="trackVehicle"
        />
      </div>

      <p v-else class="p-4 text-sm text-muted-foreground">{{ emptyMessage }}</p>

      <!-- The ETA is the operator's own estimate, not a schedule. Saying so is
           the honest framing; the reference app carries the same caveat. -->
      <p v-if="vehicles.length > 0" class="px-3 py-3 text-xs text-muted-foreground">
        Arrival times are estimates from the operator and are affected by traffic.
      </p>
    </div>
  </StopScreenShell>
</template>
